/* RefundDetailPage.js — «ریز استرداد» (kind=1: عمرانی/تملک سرمایه ای، kind=2: جاری/هزینه ای)
   + سابقه استرداد + انتقال به برگه استردادی دیگر.
   همهٔ کوئری‌ها، کنترل‌ها و پیام‌ها در بک‌اند (deposit/views/refund_detail.py) است؛ این صفحه فقط
   حالت‌های فرم (نمایش / جدید / ویرایش)، کلیدهای F1 / F2 / F11 / Esc و تأییدهای «خیر/بلی» را دارد. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => n === null || n === undefined ? '' : String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v = Number(n || 0); return v === 0 ? '۰' : v.toLocaleString('fa-IR'); };
const toEn   = v => String(v ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
const num    = v => { const n = parseInt(toEn(v).replace(/,/g, ''), 10); return isNaN(n) ? 0 : n; };
const API = (path, opts = {}) => fetch(`/api/deposit${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` }, ...opts,
}).then(jsonOf, netErr);
const NO_ROWS = [];
const LATIN = /^[a-zA-Z0-9. \-_?]*$/;                     // سرستون لاتین پنهان می‌شود
const MONEY = ['مبلغ سپرده', 'استرداد فعلی', 'سایر استرداد', 'مانده', 'سپرده', 'جمع استرداد قبلی',
  'بدهی صورت وضعیت قطعی', 'مبلغ استرداد', 'مبلغ صورت وضعیت', 'علی الحساب', 'پیش پرداخت', 'مبلغ حواله',
  'مانده قابل پرداخت', 'جمع کسورات', 'مبلغ قرارداد', 'استرداد', 'مانده استردادنشده', 'مبلغ'];

/* اعتبارسنجی شماره شبا */
export function checkSheba(v) {
  let x = String(v || '').trim().replace(/ /g, '');
  if (x.length < 24 || x.length > 26 || x.length === 25) return false;
  if (x.slice(0, 2).toUpperCase() !== 'IR') x = 'IR' + x;
  if (x.length !== 26 || !/^\d+$/.test(x.slice(2))) return false;
  try { return BigInt(x.slice(4) + '1827' + x.slice(2, 4)) % 97n === 1n; } catch (e) { return false; }
}

/* ستون‌های قابل‌نمایش: لاتین پنهان، و ستون فارسی که در همهٔ ردیف‌ها خالی/صفر است پنهان */
function visibleCols(cols, rows) {
  return (cols || []).filter(c => !LATIN.test(c) &&
    rows.some(r => r[c] !== '' && r[c] !== 0 && r[c] !== null && r[c] !== undefined));
}
const cell = (c, v) => MONEY.includes(c) ? numFmt(v) : toFa(v);

/* تأییدهای دومرحله‌ای سرور؛ پاسخ «خیر» در cheque_no و rest هم ادامه دارد */
async function withConfirms(url, payload) {
  let confirms = {};
  for (let k = 0; k < 8; k++) {
    const j = await API(url, { method: 'POST', body: JSON.stringify({ ...payload, confirms }) });
    if (!j.confirm) return j;
    const yes = window.confirm(j.confirm.msg);
    if (!yes && !['cheque_no', 'rest'].includes(j.confirm.key)) return null;
    confirms = { ...confirms, [j.confirm.key]: yes };
  }
  return null;
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 900, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '10px' },
  page: { background: '#f4f6f9', borderRadius: '14px', width: '100%', height: '96vh', display: 'flex',
    flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', fontFamily: "'Vazirmatn',Tahoma,sans-serif" },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px',
    background: 'linear-gradient(135deg,#0a1628,#1a2f50)', borderRadius: '14px 14px 0 0', color: '#fff' },
  close: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
    width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' },
  bar: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px',
    background: '#fff', borderBottom: '1px solid #e5e5e5' },
  btn: c => ({ padding: '7px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '12px', fontWeight: 700, color: '#fff', background: c }),
  body: { flex: 1, overflow: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  card: { background: '#fff', borderRadius: '10px', padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: '8px 10px' },
  label: { fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '3px' },
  input: ro => ({ padding: '6px 8px', border: '1.5px solid #e0e0e0', borderRadius: '7px', fontSize: '12.5px',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', background: ro ? '#f5f5f5' : '#fff' }),
  th: { padding: '7px 8px', background: '#1a2f50', color: '#fff', fontSize: '11px', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1 },
  td: { padding: '5px 8px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' },
  err: { background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '8px 12px',
    color: '#c62828', fontSize: '13px', whiteSpace: 'pre-line' },
  modal: { background: '#fff', borderRadius: '12px', width: '100%', maxHeight: '90vh', display: 'flex',
    flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  drop: { position: 'absolute', top: '100%', right: 0, zIndex: 50, background: '#fff', border: '1px solid #ccc',
    borderRadius: '8px', maxHeight: '260px', overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', minWidth: '100%' },
};

/* ───────── اجزای بیرون از کامپوننت (پرش فوکوس نداشته باشد) ───────── */
const Field = ({ label, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <div style={st.label}>{label}</div>{children}
  </div>
);
const Txt = ({ value, onChange, ro, dir, onBlur, onKeyDown, inputRef, onDoubleClick, placeholder, color }) => (
  <input ref={inputRef} value={value ?? ''} readOnly={ro} onChange={ro ? undefined : e => onChange(e.target.value)}
    onBlur={onBlur} onKeyDown={onKeyDown} onDoubleClick={onDoubleClick} placeholder={placeholder}
    style={{ ...st.input(ro), direction: dir || 'rtl', color: color || 'inherit' }} />
);

function Table({ cols, rows, selKey, onPick, onDouble, rowStyle, maxH = '38vh' }) {
  return (
    <div style={{ overflow: 'auto', maxHeight: maxH, border: '1px solid #eee', borderRadius: '8px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{cols.map(c => <th key={c} style={st.th}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={{ ...st.td, color: '#999', padding: '16px' }}>
            اطلاعاتی برای نمایش وجود ندارد</td></tr>}
          {rows.map((r, k) => {
            const sel = selKey !== undefined && selKey === k;
            const extra = rowStyle ? rowStyle(r) : {};
            return (
              <tr key={k} onClick={() => onPick && onPick(r, k)} onDoubleClick={() => onDouble && onDouble(r, k)}
                style={{ cursor: 'pointer', background: sel ? '#64b5f6' : (extra.background || (k % 2 ? '#fdf6e3' : '#fff')),
                  color: sel ? '#000' : extra.color }}>
                {cols.map(c => <td key={c} style={{ ...st.td, textAlign: MONEY.includes(c) ? 'left' : 'center' }}>
                  {cell(c, r[c])}</td>)}
              </tr>);
          })}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ title, onClose, children, footer, width = '900px' }) {
  return (
    <div style={{ ...st.overlay, zIndex: 1000 }} onClick={onClose}>
      <div style={{ ...st.modal, maxWidth: width }} onClick={e => e.stopPropagation()}
        onContextMenu={e => { e.preventDefault(); onClose(); }}>
        <div style={{ ...st.head, borderRadius: '12px 12px 0 0' }}><b style={{ fontSize: '13px' }}>{title}</b>
          <button onClick={onClose} style={st.close}>✕</button></div>
        <div style={{ padding: '12px', overflow: 'auto', flex: 1 }}>{children}</div>
        {footer && <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '10px 12px',
          borderTop: '1px solid #eee', alignItems: 'center' }}>{footer}</div>}
      </div>
    </div>
  );
}

/* کادر جستجو با فهرست کشویی */
function Lookup({ value, ro, onText, fetcher, onPick, show = ['کد', 'عنوان'], dir }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const change = v => {
    onText(v);
    clearTimeout(timer.current);
    if (!v.trim()) { setList([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      const j = await fetcher(v);
      setList(j?.data || []); setOpen(true);
    }, 300);
  };
  return (
    <div style={{ position: 'relative' }}>
      <Txt value={value} ro={ro} onChange={change} dir={dir}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }} />
      {open && !ro && list.length > 0 && (
        <div style={st.drop}>
          {list.map((r, k) => (
            <div key={k} onMouseDown={() => { onPick(r); setOpen(false); }}
              style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3',
                whiteSpace: 'nowrap', display: 'flex', gap: '10px' }}>
              {show.map(c => <span key={c}>{cell(c, r[c])}</span>)}
            </div>))}
        </div>)}
    </div>
  );
}

/* ══════════════════ فرم اصلی ══════════════════ */
const EMPTY1 = { id: 0, contract: '', contract_date: '', subject: '', end_date: '', mofasa: '', debit: 0,
  contractor: '', contract_id: 0, contractor_code: 0, cheque_date: '', cheque_no: '', credit_no: '', credit_year: '',
  credit_row: '', amount: '', last: '', remain: '', new_refund: '', taker: '', havaleh_id: 0, havaleh_text: '',
  release_id: 0, release_text: '', sanad: '', sanad_date: '', is_paid: true };
const EMPTY2 = { id: 0, cheque_date: '', cheque_no: '', credit_no: '', credit_year: '', month: '', credit_row: '',
  amount: '', last: '', remain: '', new_refund: '', slip_date: '', remark: '', receipt_no: '', slip_no: '', case_no: '',
  sanad: '', sanad_date: '', request_no: '', request_date: '', taker: '', melli_code: '', slip_id: '', tel: '',
  account_no: '', branch_code: 0, branch_text: '', is_paid: true, percent: 0 };

export default function RefundDetailPage({ refundId, kind, onClose }) {
  const base = `/refunds/${refundId}/rd${kind}`;
  const [res, setRes] = useState(null);             // پاسخ گرید: refund, caps, columns, data
  const [err, setErr] = useState('');
  const [idx, setIdx] = useState(-1);
  const [mode, setMode] = useState('view');         // view | new | edit
  const [f, setF] = useState(kind === 1 ? EMPTY1 : EMPTY2);
  const [info, setInfo] = useState({});             // کادر چک صادرشده و ...
  const [limit, setLimit] = useState(false);        // «ویرایش محدود»
  const [notAllowed, setNotAllowed] = useState(''); // دلیل مجاز نبودن استرداد
  const [summary, setSummary] = useState(true);     // خلاصه (فرم ۲) — پیش‌فرض روشن
  const [changeAllowed, setChangeAllowed] = useState(false);   // اجازهٔ تغییر (فرم ۲)
  const [newRefundRo, setNewRefundRo] = useState(false);
  const [q, setQ] = useState('');                   // جستجوی درون‌گرید
  const [picker, setPicker] = useState(null);       // انتخاب سپرده
  const [history, setHistory] = useState(null);     // سابقه استرداد
  const [move, setMove] = useState(null);           // انتقال به برگه دیگر
  const [releases, setReleases] = useState(null);   // انتخاب صورت وضعیت
  const [branchList, setBranchList] = useState(null);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const rows = res?.data || NO_ROWS;
  const ref = res?.refund || {};
  const caps = res?.caps || {};

  /* ── بارگذاری فهرست ── */
  const load = useCallback(async (keepId) => {
    const j = await API(`${base}/?summary=${summary ? 1 : 0}`);
    if (!j.ok) { setErr(j.error || j.msg); return; }
    setRes(j); setErr('');
    const data = j.data || [];
    let k = data.length ? 0 : -1;
    if (keepId) { const p = data.findIndex(r => r.RefundDetailId === keepId); if (p >= 0) k = p; }
    setIdx(k);
  }, [base, summary]);
  useEffect(() => { load(); }, [load]);

  /* ── نمایش ردیف انتخاب‌شده ── */
  useEffect(() => {
    if (mode !== 'view') return;
    const g = rows[idx];
    if (!g) { setF(kind === 1 ? EMPTY1 : EMPTY2); setInfo({}); return; }
    if (kind === 1) {
      setF({ ...EMPTY1, id: g.RefundDetailId, contract: g['قرارداد'], contract_date: g['تاریخ'], subject: g['موضوع'],
        end_date: g['اتمام قرارداد'], mofasa: g['مفاصاحساب بیمه'], debit: g['بدهی صورت وضعیت قطعی'],
        contractor: g['پیمانکار'], contract_id: g.ContractId, contractor_code: g.ContractorCode,
        cheque_date: g['تاریخ پرداخت'], cheque_no: g['شماره پرداخت'], credit_no: g['بستانکاری'], credit_year: g['سال'],
        credit_row: g['ردیف'], amount: g['مبلغ سپرده'], last: g['سایر استرداد'], remain: g['مانده'],
        new_refund: g['استرداد فعلی'], taker: g.TakerRaw, havaleh_id: g.HavalehId, release_id: g.ReleaseDepositOrZemanatId,
        release_text: `${g['صورت وضعیت'] ?? ''}  ${g['تاریخ صورت وضعیت'] ?? ''}`.trim(),
        sanad: g['سند'], sanad_date: g['تاریخ سند'], is_paid: Number(g.IsPaid) === 1 });
    } else {
      setF({ ...EMPTY2, id: g.RefundDetailId, cheque_date: g['تاریخ پرداخت'], cheque_no: g['شماره پرداخت'],
        credit_no: g['بستانکاری'], credit_year: g['سال'], credit_row: g['ردیف'], last: g['جمع استرداد قبلی'],
        amount: g['سپرده'], new_refund: g['استرداد فعلی'], remain: g['مانده'], slip_date: g['تاریخ'],
        remark: g['مشخصات'], receipt_no: g['قبض/شناسه واریز'], slip_no: g['فیش'], case_no: g['پرونده'],
        sanad: summary ? '' : g['سند'], sanad_date: summary ? '' : g['تاریخ سند'],
        request_no: g['درخواست'], request_date: g['تاریخ درخواست'], taker: g['دریافت کننده'], melli_code: g['کد ملی'],
        slip_id: g['شناسه قبض'], tel: g['تلفن'], account_no: g['حساب'], branch_code: g['بانک'],
        is_paid: !!Number(g.IsPaid) });
    }
    setNotAllowed('');
    API(`/refund-details/${g.RefundDetailId}/info${kind}/`).then(j => {
      if (!j.ok) return;
      setInfo(j);
      if (kind === 1) setF(p => ({ ...p, havaleh_text: j.havaleh_text || '' }));
      else setF(p => ({ ...p, branch_text: j.branch_text || '' }));
    });
  }, [idx, rows, mode, kind, summary]);

  /* ── جستجوی درون‌گرید ── */
  const filterKeys = kind === 1
    ? ['مبلغ سپرده', 'استرداد فعلی', 'موضوع', 'پیمانکار', 'قرارداد', 'شماره پرداخت', 'گیرنده']
    : ['سپرده', 'استرداد فعلی', 'شماره پرداخت', 'دریافت کننده', 'قبض/شناسه واریز', 'واحد اجرایی', 'حساب',
      'درخواست', 'مشخصات', 'پرونده'];
  const qs = toEn(q).trim().replace(/,/g, '');
  const shown = rows.map((r, k) => ({ r, k })).filter(({ r }) => !qs || filterKeys.some((c, n) =>
    n < 2 ? (/^\d+$/.test(qs) && String(r[c]) === qs) : String(r[c] ?? '').includes(qs)));
  const cols = visibleCols(res?.columns, rows);

  /* ── جدید / ویرایش ── */
  const emptyTxts = () => {
    if (kind === 1) setF({ ...EMPTY1 });
    else setF(p => ({ ...EMPTY2, request_no: p.request_no || ref.request_no, request_date: p.request_date || ref.request_date }));
    setNotAllowed(''); setInfo({});
  };
  const doNew = () => {
    setLimit(false); setNewRefundRo(kind === 2 && !changeAllowed);
    emptyTxts(); setMode('new'); setTimeout(() => firstRef.current?.focus(), 50);
  };
  const doEdit = async () => {
    if (!f.id) return;
    const j = await API(`/refund-details/${f.id}/edit-check${kind}/`, { method: 'POST' });
    if (!j.ok) { alert(j.error || j.msg); return; }
    if (j.msg) alert(j.msg);
    setLimit(!!j.limit); setNewRefundRo(kind === 2 && !changeAllowed); setMode('edit');
  };
  const doCancel = () => { setMode('view'); setPicker(null); setLimit(false); if (kind === 2) load(f.id); };

  /* ── ثبت ── */
  const save = async () => {
    if (notAllowed) { alert('استرداد این سپرده بدلیل منع استرداد امکان پذیر نیست'); return; }
    setBusy(true);
    const payload = { ...f, credit_no: num(f.credit_no), credit_year: num(f.credit_year), credit_row: num(f.credit_row),
      new_refund: num(f.new_refund), id: mode === 'edit' ? f.id : 0 };
    const j = await withConfirms(`${base}/save/`, payload);
    setBusy(false);
    if (!j) return;
    if (!j.ok) { if (j.error || j.msg) alert(j.error || j.msg); return; }
    (j.warnings || []).forEach(w => alert(w));
    if (mode === 'edit') { setMode('view'); setLimit(false); await load(j.id); }
    else { await load(j.id); emptyTxts(); setTimeout(() => firstRef.current?.focus(), 50); }
  };

  /* ── حذف ── */
  const del = async () => {
    if (!f.id) return;
    const j = await withConfirms(`/refund-details/${f.id}/delete${kind}/`, {});
    if (!j) return;
    if (!j.ok) { alert(j.error || j.msg); return; }
    load();
  };

  /* ── جستجوی ریز (F1) ── */
  const search = async () => {
    const p = kind === 1
      ? { contract_id: f.contract_id, contractor_code: f.contractor_code, credit_no: num(f.credit_no),
        credit_year: num(f.credit_year), credit_row: num(f.credit_row), detail_amount: num(f.amount) }
      : { slip_no: f.slip_no, case_no: f.case_no, receipt_no: f.receipt_no, remark: f.remark, slip_date: f.slip_date,
        month: num(f.month), credit_no: num(f.credit_no), credit_year: num(f.credit_year), credit_row: num(f.credit_row),
        detail_amount: num(f.amount) };
    const j = await API(`${base}/search/?${new URLSearchParams(p).toString()}`);
    if (!j.ok) { alert(j.error || j.msg); return; }
    if (j.data.length === 1) pickDeposit(j.data[0], true);
    else setPicker(j);
  };
  const pickDeposit = (r, single) => {
    setPicker(null);
    if (kind === 1) {
      const amount = Number(r['مبلغ سپرده'] || 0), remain = Number(r['مانده'] || 0);
      setF(p => ({ ...p, credit_no: r['بستانکاری'], credit_year: r['سال'], credit_row: r['ردیف'], amount,
        last: amount - remain, remain, contract_id: r.ContractId, contractor_code: r.ContractorCode,
        ...(single ? { end_date: r['اتمام قرارداد'], mofasa: r['مفاصاحساب بیمه'], contract: r['قرارداد'] || '',
          subject: r['موضوع'] || '', contractor: r['پیمانکار'] || '' } : {}) }));
      if (single && Number(r.IsRefundNotAllowed)) setNotAllowed(r.NotAllowedRefundReason || 'منع استرداد دارد');
    } else {
      const amount = Number(r.DetailAmount || 0), last = Number(r.SumRefundPay || 0);
      setF(p => ({ ...p, receipt_no: r.ReceiptNo, case_no: r.CaseNo, slip_no: r.SlipNo, slip_date: r.SlipDate,
        credit_no: r.CreditNo, credit_year: r.CreditYear, credit_row: r.DetailRow, amount, remark: r.Remark,
        last, remain: amount - last, new_refund: amount - last }));
      if (Number(r.IsRefundNotAllowed)) setNotAllowed(r.NotAllowedRefundReason || 'منع استرداد دارد');
      if (last > 0) openHistory({ credit_no: r.CreditNo, credit_year: r.CreditYear, credit_row: r.DetailRow, last });
    }
  };

  /* ── سابقه استرداد (F2) ── */
  const openHistory = async (o) => {
    const x = o || { credit_no: num(f.credit_no), credit_year: num(f.credit_year), credit_row: num(f.credit_row),
      last: num(f.last) };
    if (!x.credit_no || !x.credit_year || !x.credit_row) { alert('اطلاعات سپرده برای نمایش سابقه استرداد کافی نیست'); return; }
    if (!Number(x.last)) { alert('این سپرده استردادی نداشته است'); return; }
    const j = await API(`/refund-details/history/?${new URLSearchParams(x).toString()}`);
    if (!j.ok) { alert(j.error || j.msg); return; }
    setHistory(j);
  };

  /* ── انتقال به برگه استردادی دیگر ── */
  const openMove = async () => {
    if (!f.id) return;
    if (ref.closed) { alert('این استردادی بسته شده است و امکان انتقال آن وجود ندارد'); return; }
    setMove({ mode: 'one', new_no: '', new_year: ref.year, request_no: kind === 2 ? f.request_no : '', data: null });
  };

  /* ── صورت وضعیت (دابل‌کلیک) ── */
  const openReleases = async () => {
    if (mode === 'view' || !f.contract_id) return;
    const j = await API(`/refund-details/lookup1/release/?contractor_code=${f.contractor_code || 0}`);
    if (!j.ok) { alert(j.error || j.msg); return; }
    setReleases(j);
  };

  /* ── بانک/شعبه (هنگام خروج از کد شعبه) ── */
  const branchLeave = async () => {
    if (mode === 'view' || !/^\d+$/.test(toEn(f.branch_text).trim())) return;
    const j = await API(`/refund-details/lookup2/branch/?code=${toEn(f.branch_text).trim()}`);
    if (j.ok && j.code) setF(p => ({ ...p, branch_code: j.code, branch_text: j.text }));
    else setBranchList(j.ok ? j : { data: [], columns: [] });
  };
  const branchSearch = async (text) => {
    const j = await API(`/refund-details/lookup2/branch/?q=${encodeURIComponent(text || '')}`);
    setBranchList(j.ok ? j : { data: [], columns: [] });
  };

  /* ── کلیدها ── */
  const cardKey = e => {
    if (e.key === 'F1' && mode !== 'view') { e.preventDefault(); search(); }
    if (e.key === 'F2') { e.preventDefault(); openHistory(); }
    if (e.key === 'Escape' && mode !== 'view') doCancel();
  };
  const keyDown = (e, fld) => {
    if (e.key === 'F11' && mode !== 'view' && fld === 'new_refund' && num(f.new_refund) <= 100) {
      e.preventDefault();
      const pr = num(f.new_refund);
      setF(p => ({ ...p, new_refund: Math.round(num(p.amount) * pr / 100), percent: kind === 2 ? pr : 0 }));
    }
  };

  const edit = mode !== 'view';
  const T = (k, props = {}) => (
    <Txt value={f[k]} onChange={v => set(k, v)} ro={!edit || props.ro} dir={props.dir}
      onKeyDown={e => keyDown(e, k)} inputRef={props.inputRef} onBlur={props.onBlur}
      onDoubleClick={props.onDoubleClick} color={props.color} />
  );
  const D = (k, ro) => (!edit || ro)
    ? <Txt value={f[k]} ro dir="ltr" />
    : <JalaliDatePicker value={f[k]} onChange={v => set(k, v)} />;
  const M = k => <Txt value={edit ? f[k] : numFmt(f[k])} ro dir="ltr" />;   // مبلغ فقط‌خواندنی

  const rowStyle = r => (kind === 1 || !summary) && Number(r.IsPaid) === 0 ? { background: '#DDA0DD', color: '#0000FF' } : {};

  if (!res && !err) return <div style={st.overlay}><div style={{ color: '#fff' }}>⏳</div></div>;

  return (
    <div style={st.overlay}>
      <div style={st.page}>
        <div style={st.head}>
          <div>
            <b style={{ fontSize: '14px' }}>{res?.title}</b>
            <span style={{ fontSize: '12px', marginRight: '14px', color: '#C8A84B' }}>
              برگه {toFa(ref.no)} / {toFa(ref.year)} — مبلغ استرداد {numFmt(ref.amount)}
              {ref.closed ? ' — 🔒 بسته' : ''}</span>
          </div>
          <button onClick={onClose} style={st.close}>✕</button>
        </div>

        {/* ── دکمه‌ها ── */}
        <div style={st.bar}>
          {!edit && <>
            {caps.new && <button onClick={doNew} style={st.btn('#2e7d32')}>＋ جدید</button>}
            {caps.edit && <button onClick={doEdit} disabled={!f.id} style={{ ...st.btn('#1565c0'), opacity: f.id ? 1 : .4 }}>✎ ویرایش</button>}
            {caps.delete && <button onClick={del} disabled={!f.id} style={{ ...st.btn('#c62828'), opacity: f.id ? 1 : .4 }}>✕ حذف</button>}
            {caps.move && <button onClick={openMove} disabled={!f.id} style={{ ...st.btn('#6a1b9a'), opacity: f.id ? 1 : .4 }}>
              ⇄ انتقال به برگه استردادی دیگر</button>}
            {caps.history && <button onClick={() => openHistory()} style={st.btn('#455a64')}>🕘 سابقه استرداد (F2)</button>}
            {kind === 2 && <>
              <label style={{ fontSize: '12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="checkbox" checked={summary} onChange={e => setSummary(e.target.checked)} />خلاصه</label>
              <label style={{ fontSize: '12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="checkbox" checked={changeAllowed} onChange={e => setChangeAllowed(e.target.checked)} />قابل تغییر</label>
            </>}
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجو"
              style={{ ...st.input(false), width: '200px', marginRight: 'auto' }} />
            <span style={{ fontSize: '12px' }}>تعداد: <b>{toFa(rows.length)}</b></span>
          </>}
          {edit && <>
            {caps.ok && <button onClick={save} disabled={busy} style={st.btn('#2e7d32')}>{busy ? '⏳' : '✔ ثبت'}</button>}
            <button onClick={doCancel} style={st.btn('#757575')}>لغو</button>
            {caps.eraser && <button onClick={() => { setPicker(null); emptyTxts(); }} style={st.btn('#8d6e63')}>پاک کردن</button>}
            {caps.search && <button onClick={search} style={st.btn('#ef6c00')}>جستجو F1</button>}
            <span style={{ fontSize: '12px', color: '#666' }}>
              {mode === 'new' ? 'ثبت ریز جدید' : 'ویرایش ریز'} — F1 جستجوی سپرده ، F11 در «استرداد فعلی» = درصد از مبلغ سپرده ، Esc لغو</span>
          </>}
        </div>

        <div style={st.body}>
          {err && <div style={st.err}>{err}</div>}

          {/* ── فیلدها ── */}
          <div style={st.card} onKeyDown={cardKey}>
            {notAllowed && <div onClick={() => alert(notAllowed)}
              style={{ ...st.err, cursor: 'pointer', marginBottom: '8px', fontWeight: 700 }}>منع استرداد دارد</div>}
            {kind === 1 ? (
              <div style={st.grid}>
                <Field label="قرارداد" span={2}>
                  <Lookup value={f.contract} ro={!edit} dir="rtl" onText={v => set('contract', v)}
                    fetcher={t => API(`/refund-details/lookup1/contract/?q=${encodeURIComponent(t)}`)}
                    show={['کد', 'تاریخ', 'عنوان', 'پیمانکار']}
                    onPick={async r => {
                      setF(p => ({ ...p, contract_id: r.Id, contract: r['کد'], contract_date: r['تاریخ'], subject: r['عنوان'] }));
                      const j = await API(`/refund-details/lookup1/contract-info/?id=${r.Id}`);
                      if (j.ok) setF(p => ({ ...p, contractor_code: j.contractor_code, contractor: j.contractor }));
                    }} />
                </Field>
                <Field label="تاریخ قرارداد">{T('contract_date', { ro: true, dir: 'ltr' })}</Field>
                <Field label="پیمانکار" span={2}>
                  <Lookup value={f.contractor} ro={!edit} onText={v => setF(p => ({ ...p, contractor: v, contractor_code: 0 }))}
                    fetcher={t => API(`/refund-details/lookup1/contractor/?q=${encodeURIComponent(t)}`)}
                    onPick={r => setF(p => ({ ...p, contractor_code: r.Id, contractor: r['عنوان'] }))} />
                </Field>
                <Field label="موضوع" span={3}><Txt value={f.subject} ro /></Field>
                {f.end_date ? <Field label="اتمام قرارداد"><Txt value={f.end_date} ro dir="ltr" /></Field> : null}
                {f.mofasa ? <Field label="مفاصاحساب بیمه"><Txt value={f.mofasa} ro dir="ltr" /></Field> : null}
                {Number(f.debit) ? <Field label="بدهی صورت وضعیت قطعی"><Txt value={numFmt(f.debit)} ro dir="ltr" color="#c62828" /></Field> : null}
                <Field label="بستانکاری">{T('credit_no', { dir: 'ltr', inputRef: firstRef })}</Field>
                <Field label="سال">{T('credit_year', { dir: 'ltr', onBlur: () => { const y = num(f.credit_year); if (y) set('credit_year', y < 10 ? 1400 + y : y < 100 ? 1300 + y : y < 1000 ? 1000 + y : y); } })}</Field>
                <Field label="ردیف">{T('credit_row', { dir: 'ltr' })}</Field>
                <Field label="مبلغ سپرده">{T('amount', { dir: 'ltr' })}</Field>
                <Field label="سایر استرداد">{M('last')}</Field>
                <Field label="مانده">{M('remain')}</Field>
                <Field label="استرداد فعلی">{T('new_refund', { dir: 'ltr', ro: limit })}</Field>
                <Field label="شماره پرداخت">{T('cheque_no', { dir: 'ltr', ro: limit })}</Field>
                <Field label="تاریخ پرداخت">{D('cheque_date', limit)}</Field>
                <Field label="گیرنده" span={2}>{T('taker')}</Field>
                <Field label="حواله تامین اعتبار نشده" span={3}>
                  <Lookup value={f.havaleh_text} ro={!edit || !f.contractor_code || info.havaleh_funded}
                    onText={v => { if (!v) setF(p => ({ ...p, havaleh_id: 0, havaleh_text: '' })); else set('havaleh_text', v); }}
                    fetcher={t => API(`/refund-details/lookup1/havaleh/?q=${encodeURIComponent(t)}&contract_id=${f.contract_id || 0}&contractor_code=${f.contractor_code || 0}`)}
                    show={['کد', 'عنوان', 'مانده قابل پرداخت']}
                    onPick={r => setF(p => ({ ...p, havaleh_id: r.Id, havaleh_text: r.text }))} />
                </Field>
                <Field label="صورت وضعیت (دابل‌کلیک)" span={2}>
                  <Txt value={f.release_text} ro onDoubleClick={openReleases} />
                </Field>
                <Field label="سند / تاریخ سند"><Txt value={f.sanad ? `${toFa(f.sanad)}  ${toFa(f.sanad_date)}` : ''} ro /></Field>
                <Field label=" ">
                  <label style={{ fontSize: '12px', display: 'flex', gap: '6px', alignItems: 'center', paddingTop: '6px' }}>
                    <input type="checkbox" checked={!!f.is_paid} disabled={!edit} onChange={e => set('is_paid', e.target.checked)} />پرداخت شد</label>
                </Field>
              </div>
            ) : (
              <div style={st.grid}>
                <Field label="قبض/شناسه واریز" span={2}>{T('receipt_no', { dir: 'ltr', inputRef: res?.khazane ? undefined : firstRef })}</Field>
                <Field label="پرونده">{T('case_no')}</Field>
                <Field label="فیش">{T('slip_no', { dir: 'ltr' })}</Field>
                <Field label="تاریخ فیش">{D('slip_date')}</Field>
                {edit && <Field label="ماه">{T('month', { dir: 'ltr' })}</Field>}
                <Field label="بستانکاری">{T('credit_no', { dir: 'ltr', inputRef: res?.khazane ? firstRef : undefined })}</Field>
                <Field label="سال">{T('credit_year', { dir: 'ltr', onBlur: () => { const y = num(f.credit_year); if (y) set('credit_year', y < 10 ? 1400 + y : y < 100 ? 1300 + y : y < 1000 ? 1000 + y : y); } })}</Field>
                <Field label="ردیف">{T('credit_row', { dir: 'ltr' })}</Field>
                <Field label="سپرده">{T('amount', { dir: 'ltr' })}</Field>
                <Field label="جمع استرداد قبلی">{M('last')}</Field>
                <Field label="مانده">{M('remain')}</Field>
                <Field label="استرداد فعلی">{T('new_refund', { dir: 'ltr', ro: newRefundRo })}</Field>
                <Field label="مشخصات" span={3}>{T('remark')}</Field>
                <Field label="شماره پرداخت">{T('cheque_no', { dir: 'ltr' })}</Field>
                <Field label="تاریخ پرداخت">{D('cheque_date')}</Field>
                <Field label="درخواست">{T('request_no', { dir: 'ltr' })}</Field>
                <Field label="تاریخ درخواست">{D('request_date')}</Field>
                <Field label="دریافت کننده" span={2}>
                  <Lookup value={f.taker} ro={!edit || limit} onText={v => set('taker', v)} show={['شرح']}
                    fetcher={t => API(`/refund-details/lookup2/Taker/?q=${encodeURIComponent(t)}`)}
                    onPick={async r => {
                      set('taker', r['شرح']);
                      if (!f.melli_code) {
                        const j = await API(`/refund-details/lookup2/melli-by-taker/?q=${encodeURIComponent(r['شرح'])}`);
                        if (j.ok && j.value) set('melli_code', j.value);
                      }
                    }} />
                </Field>
                <Field label="کد ملی">
                  <Lookup value={f.melli_code} ro={!edit} dir="ltr" onText={v => set('melli_code', v)} show={['شرح']}
                    fetcher={t => API(`/refund-details/lookup2/MelliCode/?q=${encodeURIComponent(t)}`)}
                    onPick={async r => {
                      set('melli_code', r['شرح']);
                      if (!f.taker) {
                        const j = await API(`/refund-details/lookup2/taker-by-melli/?q=${encodeURIComponent(r['شرح'])}`);
                        if (j.ok && j.value) set('taker', j.value);
                      }
                    }} />
                </Field>
                <Field label="حساب (شبا)" span={2}>{T('account_no', { dir: 'ltr', ro: limit,
                  onBlur: () => { if (edit && String(f.account_no || '').trim() && !checkSheba(f.account_no)) alert('شماره حساب شبا اشتباه است'); } })}</Field>
                <Field label="بانک / شعبه (کد شعبه)" span={2}>
                  <Txt value={f.branch_text} ro={!edit || limit} onChange={v => set('branch_text', v)} onBlur={branchLeave}
                    onDoubleClick={() => edit && !limit && branchSearch('')} />
                </Field>
                <Field label="شناسه قبض">{T('slip_id', { dir: 'ltr' })}</Field>
                <Field label="تلفن">{T('tel', { dir: 'ltr' })}</Field>
                <Field label="سند / تاریخ سند"><Txt value={f.sanad ? `${toFa(f.sanad)}  ${toFa(f.sanad_date)}` : ''} ro /></Field>
                <Field label=" ">
                  <label style={{ fontSize: '12px', display: 'flex', gap: '6px', alignItems: 'center', paddingTop: '6px' }}>
                    <input type="checkbox" checked={!!f.is_paid} disabled={!edit} onChange={e => set('is_paid', e.target.checked)} />پرداخت شد</label>
                </Field>
              </div>
            )}
            {info.inout && !edit && (
              <div style={{ marginTop: '8px', fontSize: '12px', background: '#e8f5e9', padding: '6px 10px', borderRadius: '6px' }}>
                چک: شماره <b>{toFa(info.inout.number)}</b> — تاریخ <b>{toFa(info.inout.date)}</b> — کدحساب ردوجوه <b>{toFa(info.inout.account_code)}</b>
              </div>)}
          </div>

          {/* ── گرید ریز / انتخاب سپرده ── */}
          {picker ? (
            <div style={st.card}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px', color: '#b8860b' }}>سپرده مورد نظر را انتخاب کنید</div>
              {kind === 1 ? (() => {
                const pr = picker.data;
                const sum = c => pr.reduce((a, r) => a + Number(r[c] || 0), 0);
                const last = sum('سایر استرداد'), now = sum('استرداد فعلی'), dep = sum('مبلغ سپرده');
                return <>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span>جمع سپرده: <b>{numFmt(dep)}</b></span><span>استرداد قبلی: <b>{numFmt(last)}</b></span>
                    <span>مانده: <b>{numFmt(dep - last)}</b></span><span>استرداد فعلی: <b>{numFmt(now)}</b></span>
                    <span>مانده: <b>{numFmt(dep - last - now)}</b></span>
                  </div>
                  <Table cols={visibleCols(picker.columns, pr)} rows={pr} onDouble={r => pickDeposit(r, false)} />
                </>;
              })() : (
                <Table cols={['پرونده', 'قبض/شناسه واریز', 'فیش', 'تاریخ', 'شهرستان', 'بستانکاری', 'سال', 'ردیف', 'مبلغ',
                  'استرداد شده', 'مانده', 'ملاحظات', 'منع استرداد']}
                  rows={picker.data.map(r => ({ ...r, 'پرونده': r.CaseNo, 'قبض/شناسه واریز': r.ReceiptNo, 'فیش': r.SlipNo,
                    'تاریخ': r.SlipDate, 'شهرستان': r.Dept_Des, 'بستانکاری': r.CreditNo, 'سال': r.CreditYear, 'ردیف': r.DetailRow,
                    'مبلغ': r.DetailAmount, 'استرداد شده': numFmt(r.SumRefundPay), 'مانده': r.Remain,
                    'ملاحظات': r.Remark, 'منع استرداد': r.RefundNotAllowed }))}
                  onDouble={r => pickDeposit(r, false)} />
              )}
              <div style={{ fontSize: '11px', color: '#777', marginTop: '4px' }}>دابل‌کلیک = انتخاب ، کلیک راست / «پاک کردن» = انصراف</div>
            </div>
          ) : (
            <div style={st.card}>
              <Table cols={cols} rows={shown.map(x => x.r)} rowStyle={rowStyle}
                selKey={shown.findIndex(x => x.k === idx)}
                onPick={(r, k) => { if (!edit) setIdx(shown[k].k); }}
                onDouble={() => { if (!edit && caps.edit) doEdit(); }} maxH="42vh" />
            </div>
          )}
        </div>
      </div>

      {/* ══ سابقه استرداد ══ */}
      {history && (
        <Modal title="سابقه استرداد" onClose={() => setHistory(null)} width="1100px"
          footer={<span style={{ fontSize: '12px', display: 'flex', gap: '18px' }}>
            <span>جمع سپرده: <b>{numFmt(history.sum_detail)}</b></span>
            <span>جمع استرداد: <b>{numFmt(history.sum_refund)}</b></span>
            <span>مانده: <b>{numFmt(history.remain)}</b></span></span>}>
          <Table cols={visibleCols(history.columns, history.data)} rows={history.data} maxH="60vh" />
        </Modal>)}

      {/* ══ انتخاب صورت وضعیت ══ */}
      {releases && (
        <Modal title="صورت وضعیت" onClose={() => setReleases(null)} width="1100px">
          <Table cols={visibleCols(releases.columns, releases.data)} rows={releases.data} maxH="60vh"
            onDouble={r => { setF(p => ({ ...p, release_id: r.ReleaseDepositOrZemanatId,
              release_text: `${r['صورت وضعیت'] ?? ''}  ${r['تاریخ صورت وضعیت'] ?? ''}` })); setReleases(null); }} />
        </Modal>)}

      {/* ══ بانک/شعبه ══ */}
      {branchList && (
        <Modal title="انتخاب شعبه بانک" onClose={() => setBranchList(null)} width="700px">
          <input autoFocus placeholder="نام بانک یا شعبه" style={{ ...st.input(false), marginBottom: '8px' }}
            onChange={e => branchSearch(e.target.value)} />
          <Table cols={['کد', 'بانک', 'شعبه']} rows={branchList.data || []} maxH="55vh"
            onDouble={r => { setF(p => ({ ...p, branch_code: r.Id, branch_text: r.text })); setBranchList(null); }} />
        </Modal>)}

      {/* ══ انتقال به برگه استردادی دیگر ══ */}
      {move && <MoveDialog kind={kind} rdid={f.id} move={move} setMove={setMove}
        onDone={() => { setMove(null); load(); }} />}
    </div>
  );
}

/* انتقال به برگه استردادی دیگر */
function MoveDialog({ kind, rdid, move, setMove, onDone }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const refresh = useCallback(async () => {
    const p = new URLSearchParams({ mode: move.mode, request_no: move.request_no || '', new_no: num(move.new_no),
      new_year: num(move.new_year) }).toString();
    const j = await API(`/refund-details/${rdid}/move${kind}/?${p}`);
    if (j.ok) { setD(j); setErr(''); } else setErr(j.error || j.msg);
  }, [kind, rdid, move.mode, move.request_no, move.new_no, move.new_year]);
  useEffect(() => { refresh(); }, [refresh]);
  const h = d?.info || {};
  const canSave = d?.can_save && d?.target_exists && num(move.new_no) > 0 &&
    (num(move.new_no) !== Number(h.RefundNo) || num(move.new_year) !== Number(h.RefundYear));
  const save = async () => {
    const j = await API(`/refund-details/${rdid}/move${kind}/`, { method: 'POST', body: JSON.stringify(move) });
    if (!j.ok) { setErr(j.error || j.msg); return; }
    onDone();
  };
  const m = (k, v) => setMove(p => ({ ...p, [k]: v }));
  return (
    <Modal title="انتقال به برگه استردادی دیگر" onClose={() => setMove(null)} width="640px"
      footer={<>{canSave && <button onClick={save} style={st.btn('#2e7d32')}>ثبت</button>}
        <button onClick={() => setMove(null)} style={st.btn('#757575')}>بستن</button></>}>
      {err && <div style={st.err}>{err}</div>}
      {d && <>
        <div style={st.grid}>
          <Field label="برگه استرداد فعلی"><Txt value={`${toFa(h.RefundNo)} / ${toFa(h.RefundYear)}`} ro /></Field>
          <Field label="بستانکاری / سال / ردیف"><Txt value={`${toFa(h.CreditNo)} / ${toFa(h.CreditYear)} / ${toFa(h.DetailRow)}`} ro /></Field>
          <Field label="فیش / تاریخ"><Txt value={`${toFa(h.SlipNo)}  ${toFa(h.SlipDate)}`} ro /></Field>
          <Field label="مبلغ استرداد"><Txt value={numFmt(move.mode === 'one' ? h.RefundPay : d.sum_refund)} ro dir="ltr" /></Field>
        </div>
        <div style={{ display: 'flex', gap: '16px', margin: '12px 0', fontSize: '12.5px', flexWrap: 'wrap' }}>
          {[['one', 'فقط همین ریز'], ['request', 'همه ریزهای این درخواست'], ['cheque', 'همه ریزهای این شماره پرداخت']].map(([k, l]) => (
            <label key={k} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input type="radio" checked={move.mode === k} onChange={() => m('mode', k)} />{l}</label>))}
        </div>
        {move.mode === 'request' && <Field label="درخواست"><Txt value={move.request_no} onChange={v => m('request_no', v)} dir="ltr" /></Field>}
        {move.mode !== 'one' && <div style={{ fontSize: '12px', margin: '6px 0' }}>تعداد: <b>{toFa(d.count)}</b></div>}
        <div style={{ ...st.grid, marginTop: '8px' }}>
          <Field label="شماره برگه مقصد"><Txt value={move.new_no} onChange={v => m('new_no', v)} dir="ltr" /></Field>
          <Field label="سال برگه مقصد"><Txt value={move.new_year} onChange={v => m('new_year', v)} dir="ltr" /></Field>
        </div>
      </>}
    </Modal>
  );
}

/* اجزای مشترک برای ریز سپرده (CreditDetailList.js) */
export { Field, Txt, Table, Modal, Lookup, withConfirms, visibleCols, st, toFa, numFmt, num, toEn, cell };
